import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Sparkles, UploadCloud, Rocket, Loader2, ArrowRight, ShieldCheck,
  FileSpreadsheet, IndianRupee,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { UploadDataDialog } from "@/components/UploadDataDialog";
import { useWorkspace } from "@/context/WorkspaceContext";
import api from "@/lib/api";

/**
 * `InstantDemoHero` — the "get to answer in 90 seconds" entry point.
 *
 * Renders two prominent CTAs on the sandbox start screen:
 *   • "Try with your own file" (CSV / XLSX / Parquet) — opens the existing
 *     `UploadDataDialog`, which already handles chunked upload + pipeline
 *     poll and hands the user to /chat when the source is queryable.
 *   • "Try the India fintech sample" — one-click seed of an obviously
 *     synthetic transactions + customers dataset. Same background pipeline;
 *     when the connect pipeline finishes we auto-navigate to /chat.
 *
 * Both flows create a NEW workspace so the demo never pollutes a customer's
 * production workspace.
 */
export function InstantDemoHero({ onSourceReady }) {
  const { active, refresh, setActive } = useWorkspace();
  const navigate = useNavigate();
  const [samples, setSamples] = useState([]);
  const [seeding, setSeeding] = useState(null);

  useEffect(() => {
    let cancelled = false;
    api.get("/uploads/samples").then(({ data }) => {
      if (!cancelled) setSamples(data.samples || []);
    }).catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const seedSample = async (key) => {
    setSeeding(key);
    try {
      const { data } = await api.post(`/uploads/sample/${key}`, { destination: "new" });
      toast.info("Preparing your demo workspace…");
      const jobId = data.job_id;
      const deadline = Date.now() + 90_000;
      let consecutiveErrors = 0;
      while (Date.now() < deadline) {
        try {
          const { data: job } = await api.get(`/jobs/${jobId}`);
          consecutiveErrors = 0;
          if (job.status === "completed") break;
          if (job.status === "failed") throw new Error(job.error || "Pipeline failed.");
        } catch (e) {
          if (e.message === "Pipeline failed.") throw e;
          consecutiveErrors += 1;
          if (consecutiveErrors >= 5) {
            throw new Error("Could not read pipeline status. Please try again.");
          }
        }
        await new Promise((r) => setTimeout(r, 1500));
      }
      await refresh();
      setActive(data.workspace_id);
      onSourceReady?.(data);
      toast.success("Demo dataset ready — ask a question.");
      navigate("/chat");
    } catch (e) {
      const detail = e?.response?.data?.detail || e.message || "Could not seed sample.";
      toast.error(detail);
    } finally {
      setSeeding(null);
    }
  };

  const fintech = samples.find((s) => s.key === "india_fintech");

  return (
    <section
      data-testid="instant-demo-hero"
      className="relative mt-8 overflow-hidden rounded-2xl border border-accent/40 bg-card/60 p-6 backdrop-blur sm:p-8"
    >
      <div aria-hidden="true" className="pointer-events-none absolute -right-24 -top-32 h-80 w-80 rounded-full bg-accent/15 blur-3xl" />
      <div aria-hidden="true" className="pointer-events-none absolute -left-16 -bottom-24 h-56 w-56 rounded-full bg-accent/10 blur-3xl" />

      <div className="relative">
        <p className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.22em] text-accent">
          <Sparkles className="h-3 w-3" />
          instant demo · no warehouse required
        </p>
        <h2 className="mt-3 text-xl font-semibold sm:text-2xl">
          From file to signed evidence pack, in under 90 seconds.
        </h2>
        <p className="mt-2 max-w-2xl text-xs leading-relaxed text-muted-foreground sm:text-sm">
          Drop a spreadsheet or start with our synthetic Indian-fintech sample. Sema will
          infer the schema, generate the auto-glossary, register the source, and open chat.
        </p>

        <div className="mt-6 grid gap-4 sm:grid-cols-2">
          <div className="rounded-xl border border-border/70 bg-background/40 p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <FileSpreadsheet className="h-4 w-4 text-accent" />
              your data
            </div>
            <h3 className="mt-2 text-base font-medium">Try with your own file</h3>
            <p className="mt-1 text-xs text-muted-foreground">
              Upload a <span className="font-mono">.csv</span>, <span className="font-mono">.xlsx</span>, or
              <span className="font-mono"> .parquet</span> file. We handle the rest.
            </p>
            <UploadDataDialog
              workspaceId={active?.id}
              onUploaded={onSourceReady}
              defaultDestination="new"
            >
              <Button
                data-testid="instant-demo-upload-btn"
                className="mt-4 h-11 w-full rounded-full bg-accent text-xs uppercase tracking-[0.16em] text-accent-foreground hover:bg-accent/90"
              >
                <UploadCloud className="mr-2 h-4 w-4" />
                Try with your own file
              </Button>
            </UploadDataDialog>
            <p className="mt-2 flex items-center gap-1 text-[10px] text-muted-foreground">
              <ShieldCheck className="h-3 w-3" />
              PII columns are auto-flagged on ingest.
            </p>
          </div>

          <div className="rounded-xl border border-border/70 bg-background/40 p-5">
            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-muted-foreground">
              <IndianRupee className="h-4 w-4 text-accent" />
              synthetic data
            </div>
            <h3 className="mt-2 text-base font-medium">
              {fintech?.name || "India Fintech — Transactions & Customers"}
            </h3>
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">
              {fintech?.description ||
                "Synthetic UPI-style transactions joined to a customers table. Zero real PII."}
            </p>
            <Button
              data-testid="instant-demo-sample-btn"
              onClick={() => seedSample("india_fintech")}
              disabled={!fintech || !!seeding}
              className="mt-4 h-11 w-full rounded-full border border-accent/40 bg-background/60 text-xs uppercase tracking-[0.16em] text-accent hover:bg-accent/10"
              variant="outline"
            >
              {seeding === "india_fintech" ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" /> Preparing…
                </>
              ) : (
                <>
                  <Rocket className="mr-2 h-4 w-4" /> Try the India fintech sample
                  <ArrowRight className="ml-2 h-3.5 w-3.5" />
                </>
              )}
            </Button>
            <p className="mt-2 text-[10px] text-muted-foreground">
              Obviously fake PANs (SEMA…), UPI handles (@semaupi) — nothing regulated.
            </p>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap items-center gap-2 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
          <span className="rounded-full border border-border/60 bg-background/40 px-2 py-1">1 · Upload / seed</span>
          <ArrowRight className="h-3 w-3" />
          <span className="rounded-full border border-border/60 bg-background/40 px-2 py-1">2 · Ask a question</span>
          <ArrowRight className="h-3 w-3" />
          <span className="rounded-full border border-border/60 bg-background/40 px-2 py-1">3 · Export evidence pack</span>
        </div>
      </div>
    </section>
  );
}
